import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, MessagesSquare } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import CoachChat from '@/components/CoachChat';
import CoachKundeninfo from '@/components/CoachKundeninfo';
import { TrainerFilter } from '@/pages/CoachHeute';

/* Chip wie im Mockup (Community.dc.html): aktiv = Marke-Fläche. */
const chip = (an) => `inline-flex min-h-[44px] items-center gap-1.5 rounded-full border px-[11px] py-[5px] text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${an ? 'border-marke bg-marke text-[#140c08]' : 'border-border bg-muted text-foreground/80 hover:text-foreground'}`;

/* Nachrichtenübersicht /coach/nachrichten (Coach-Cockpit W2.7, 29.10.2026): alle Coach-Chats der sichtbaren Kunden,
   ungelesene zuerst, Filter Ungelesen · Alle. Rechts der Verlauf (derselbe Coach-Chat wie in App, CRM und Athletenansicht)
   und daneben die Kundeninfo. Ausgewählt wird über `?kunde=` (gehe), damit Links aus Heute direkt im Chat landen. */

const zeit = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');

export default function CoachNachrichten({ coach, gehe, kunde }) {
  const [filter, setFilter] = useState('alle');
  const [trainer, setTrainer] = useState(null);
  const [d, setD] = useState(null);
  const [fehler, setFehler] = useState('');
  const [neu, setNeu] = useState('');
  const lade = useCallback(() => {
    coach.get('/nachrichten', { params: { filter, ...(trainer ? { trainer } : {}) } })
      .then((r) => { setD(r.data); setFehler(''); })
      .catch((e) => setFehler(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Nachrichten nicht geladen'));
  }, [coach, filter, trainer]);
  useEffect(() => { lade(); }, [lade]);
  const chat = useMemo(() => (kunde ? {
    laden: () => coach.get(`/nachrichten/${kunde}/chat`),
    senden: (text) => coach.post(`/nachrichten/${kunde}/chat`, { text }),
    gelesen: () => coach.post(`/nachrichten/${kunde}/chat/gelesen`).then((r) => { lade(); return r; }),
  } : null), [coach, kunde, lade]);
  const waehle = (id) => gehe({ ansicht: 'nachrichten', kunde: id });

  if (fehler) return <p className="text-sm text-destructive" role="alert">{fehler}</p>;
  if (!d) return <div className="space-y-3" aria-busy="true"><Skeleton className="h-16 rounded-[14px]" /><Skeleton className="h-64 rounded-[14px]" /></div>;
  const liste = (
    <section className={`min-w-0 rounded-[14px] border border-border bg-card p-3.5 ${kunde ? 'hidden lg:block' : ''}`} aria-label="Chats" data-testid="coach-nachrichten-liste">
      <h2 className="mb-3 flex items-center gap-2 px-2 font-sans text-base font-bold">Alle Chats <span className="rounded-full border border-marke px-1.5 text-[11px] font-extrabold text-marke dark:text-[#f0a070]">{d.chats.length}</span></h2>
      <div className="flex flex-wrap items-center gap-1.5 px-2 pb-2">
        <button type="button" className={chip(filter === 'ungelesen')} aria-pressed={filter === 'ungelesen'} onClick={() => setFilter('ungelesen')} data-testid="nachrichten-ungelesen">
          Ungelesen <span className="tabular-nums opacity-70">{d.ungelesen_chats}</span>
        </button>
        <button type="button" className={chip(filter === 'alle')} aria-pressed={filter === 'alle'} onClick={() => setFilter('alle')} data-testid="nachrichten-alle">Alle</button>
      </div>
      {!d.chats.length && <p className="p-4 text-sm text-muted-foreground">{filter === 'ungelesen' ? 'Alles gelesen.' : 'Noch keine Chats.'}</p>}
      <ul className="space-y-1">
        {d.chats.map((c) => (
          <li key={c.lead_id}>
            <button type="button" onClick={() => waehle(c.lead_id)} aria-current={kunde === c.lead_id ? 'true' : undefined} data-testid={`nachrichten-chat-${c.lead_id}`}
              className={`flex min-h-[56px] w-full items-start gap-3 rounded-[10px] px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${kunde === c.lead_id ? 'coach-hinweis' : 'hover:bg-muted/60'}`}>
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline justify-between gap-2">
                  <span className={`truncate text-sm ${c.ungelesen ? 'font-extrabold' : 'font-semibold'}`}>{c.name}</span>
                  <span className="shrink-0 text-[11px] text-muted-foreground">{zeit(c.letzte.am)}</span>
                </span>
                <span className="block truncate text-xs text-muted-foreground">{c.letzte.von === 'coach' ? `${c.letzte.name || 'Coach'}: ` : ''}{c.letzte.text}</span>
              </span>
              {c.ungelesen > 0 && <span className="mt-1 shrink-0 rounded-full bg-marke px-1.5 text-[11px] font-extrabold text-[#140c08]" aria-label={`${c.ungelesen} ungelesen`}>{c.ungelesen}</span>}
            </button>
          </li>
        ))}
      </ul>
      {d.ohne_chat.length > 0 && (
        <label className="mt-3 block border-t border-border/60 p-2 text-xs font-semibold text-muted-foreground">
          Neuen Chat beginnen
          <select value={neu} onChange={(e) => { setNeu(''); if (e.target.value) waehle(e.target.value); }} data-testid="nachrichten-neu"
            className="mt-1 min-h-[44px] w-full rounded-[10px] border border-border bg-card px-3 text-sm font-semibold text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <option value="">Kunde wählen …</option>
            {d.ohne_chat.map((k) => <option key={k.lead_id} value={k.lead_id}>{k.name}</option>)}
          </select>
        </label>
      )}
    </section>
  );
  return (
    <div className="space-y-4" data-testid="coach-nachrichten">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-semibold text-muted-foreground">{d.ungelesen_nachrichten ? `${d.ungelesen_nachrichten} ungelesene Nachrichten in ${d.ungelesen_chats} Chats` : 'Keine ungelesenen Nachrichten'}</p>
        <TrainerFilter liste={d.trainer_liste} wert={trainer} onWahl={setTrainer} />
      </div>
      <div className={`grid gap-4 ${kunde ? 'xl:grid-cols-[300px_minmax(0,1fr)_280px] lg:grid-cols-[300px_minmax(0,1fr)]' : 'lg:grid-cols-[300px_minmax(0,1fr)]'}`}>
        {liste}
        {kunde ? (
          <>
            <div className="min-w-0 space-y-3">
              <button type="button" className="inline-flex min-h-[44px] items-center gap-1.5 rounded-[10px] px-2 text-sm font-semibold text-muted-foreground hover:text-foreground lg:hidden"
                onClick={() => gehe({ ansicht: 'nachrichten' })}><ArrowLeft className="h-4 w-4" aria-hidden="true" /> Alle Chats</button>
              <CoachChat key={`n-${kunde}`} {...chat} testId="coach-nachrichten-chat" />
            </div>
            <CoachKundeninfo coach={coach} leadId={kunde} gehe={gehe} ohneChatLink />
          </>
        ) : (
          <p className="hidden items-center gap-2 text-sm text-muted-foreground lg:flex"><MessagesSquare className="h-4 w-4" aria-hidden="true" /> Chat links wählen.</p>
        )}
      </div>
    </div>
  );
}
